import { useEffect } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  $createNodeSelection,
  $createRangeSelection,
  $createTextNode,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_DOWN_COMMAND,
  type LexicalNode,
} from "lexical"

function selectOrMoveAcrossDecorator(node: LexicalNode, edge: "before" | "after"): boolean {
  if (!$isDecoratorNode(node)) return false

  if (node.isKeyboardSelectable()) {
    const nodeSelection = $createNodeSelection()
    nodeSelection.add(node.getKey())
    $setSelection(nodeSelection)
    return true
  }

  const parent = node.getParent()
  if (!parent) return false

  const rangeSelection = $createRangeSelection()
  const offset = node.getIndexWithinParent() + (edge === "after" ? 1 : 0)
  rangeSelection.anchor.set(parent.getKey(), offset, "element")
  rangeSelection.focus.set(parent.getKey(), offset, "element")
  $setSelection(rangeSelection)
  return true
}

/**
 * Handles arrow key navigation around DecoratorNodes in plain text editors.
 * PlainTextPlugin doesn't register handlers for KEY_ARROW_LEFT/RIGHT_COMMAND,
 * so the cursor gets stuck when it hits a DecoratorNode (e.g. PlaceholderNode).
 */
export default function DecoratorArrowNavigationPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const removeLeft = editor.registerCommand(
      KEY_ARROW_LEFT_COMMAND,
      () => {
        const selection = $getSelection()

        // Case 1: Decorator is already selected → move cursor before it
        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes()
          if (nodes.length === 1 && $isDecoratorNode(nodes[0])) {
            const node = nodes[0]
            const prevSibling = node.getPreviousSibling()

            if (prevSibling && $isDecoratorNode(prevSibling)) {
              selectOrMoveAcrossDecorator(prevSibling, "before")
            } else if (prevSibling) {
              const rangeSelection = $createRangeSelection()
              const textLength = prevSibling.getTextContentSize()
              rangeSelection.anchor.set(prevSibling.getKey(), textLength, "text")
              rangeSelection.focus.set(prevSibling.getKey(), textLength, "text")
              $setSelection(rangeSelection)
            } else {
              const parent = node.getParent()
              if (parent) {
                const rangeSelection = $createRangeSelection()
                rangeSelection.anchor.set(parent.getKey(), 0, "element")
                rangeSelection.focus.set(parent.getKey(), 0, "element")
                $setSelection(rangeSelection)
              }
            }
            return true
          }
        }

        // Case 2: Cursor in text node at position 0, previous sibling is decorator
        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const anchorNode = selection.anchor.getNode()
          const anchorOffset = selection.anchor.offset

          if ($isTextNode(anchorNode) && anchorOffset === 0) {
            const prevSibling = anchorNode.getPreviousSibling()
            if (prevSibling && $isDecoratorNode(prevSibling)) {
              return selectOrMoveAcrossDecorator(prevSibling, "before")
            }
          }

          // Case 3: Cursor at element boundary, child before is decorator
          if (selection.anchor.type === "element" && $isElementNode(anchorNode)) {
            const children = anchorNode.getChildren()
            if (anchorOffset > 0) {
              const childBefore = children[anchorOffset - 1]
              if ($isDecoratorNode(childBefore)) {
                return selectOrMoveAcrossDecorator(childBefore, "before")
              }
            }
          }
        }

        return false
      },
      COMMAND_PRIORITY_LOW
    )

    const removeRight = editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      () => {
        const selection = $getSelection()

        // Case 1: Decorator is already selected → move cursor after it
        if ($isNodeSelection(selection)) {
          const nodes = selection.getNodes()
          if (nodes.length === 1 && $isDecoratorNode(nodes[0])) {
            const node = nodes[0]
            const nextSibling = node.getNextSibling()

            if (nextSibling && $isDecoratorNode(nextSibling)) {
              selectOrMoveAcrossDecorator(nextSibling, "after")
            } else if (nextSibling) {
              const rangeSelection = $createRangeSelection()
              rangeSelection.anchor.set(nextSibling.getKey(), 0, "text")
              rangeSelection.focus.set(nextSibling.getKey(), 0, "text")
              $setSelection(rangeSelection)
            } else {
              const parent = node.getParent()
              if (parent) {
                const rangeSelection = $createRangeSelection()
                const childrenSize = parent.getChildrenSize()
                rangeSelection.anchor.set(parent.getKey(), childrenSize, "element")
                rangeSelection.focus.set(parent.getKey(), childrenSize, "element")
                $setSelection(rangeSelection)
              }
            }
            return true
          }
        }

        // Case 2: Cursor in text node at end, next sibling is decorator
        if ($isRangeSelection(selection) && selection.isCollapsed()) {
          const anchorNode = selection.anchor.getNode()
          const anchorOffset = selection.anchor.offset

          if ($isTextNode(anchorNode) && anchorOffset === anchorNode.getTextContentSize()) {
            const nextSibling = anchorNode.getNextSibling()
            if (nextSibling && $isDecoratorNode(nextSibling)) {
              return selectOrMoveAcrossDecorator(nextSibling, "after")
            }
          }

          // Case 3: Cursor at element boundary, child after is decorator
          if (selection.anchor.type === "element" && $isElementNode(anchorNode)) {
            const children = anchorNode.getChildren()
            if (anchorOffset < children.length) {
              const childAfter = children[anchorOffset]
              if ($isDecoratorNode(childAfter)) {
                return selectOrMoveAcrossDecorator(childAfter, "after")
              }
            }
          }
        }

        return false
      },
      COMMAND_PRIORITY_LOW
    )

    // When a decorator is selected and the user types a printable character,
    // move the cursor before the decorator so normal input inserts there.
    const removeKeyDown = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return false

        const selection = $getSelection()
        if (!$isNodeSelection(selection)) return false

        const nodes = selection.getNodes()
        if (nodes.length !== 1 || !$isDecoratorNode(nodes[0])) return false

        const node = nodes[0]
        const prevSibling = node.getPreviousSibling()

        if (prevSibling && $isTextNode(prevSibling)) {
          const rangeSelection = $createRangeSelection()
          const len = prevSibling.getTextContentSize()
          rangeSelection.anchor.set(prevSibling.getKey(), len, "text")
          rangeSelection.focus.set(prevSibling.getKey(), len, "text")
          $setSelection(rangeSelection)
        } else {
          // Insert an empty text node before the decorator for the cursor to land in
          const textNode = $createTextNode("")
          node.insertBefore(textNode)
          textNode.selectEnd()
        }

        return false // let the normal input flow handle the character
      },
      COMMAND_PRIORITY_LOW
    )

    return () => {
      removeLeft()
      removeRight()
      removeKeyDown()
    }
  }, [editor])

  return null
}
